import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    Signal,
    ViewEncapsulation,
    model,
    AfterViewInit,
} from '@angular/core';
import { IOnboardingReportParticipantDetails } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import {
    IOnboardingCompletionStep,
    IOnboardingCompletionUserStep,
    ICurrentOpenedCompletionStepData,
} from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { FiOnboardingCompletionStateType } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-enums';
import { FiProfileSizes, IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { IOnboardingCompletionSection } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { getIconClassByObjectType } from '@fi-shared/utils/get-icon-class-by-object-type';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { IOnboardingActive } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingReportActionsBuilderService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-actions-builder.service';
import { isDefined } from '@fi-shared/utils';
import { FiOnboardingCompletionHelperService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-helper.service';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingCompletionSyncService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-sync.service';
@Component({
    selector: 'fi-onboarding-report-participant-details',
    templateUrl: './fi-onboarding-report-participant-details.component.html',
    styleUrl: './fi-onboarding-report-participant-details.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-report-participant-details' },
    standalone: false,
})
export class FiOnboardingReportParticipantDetailsComponent implements OnInit, AfterViewInit {
    // @Input() currentFilter: IOnboardingReportWidgetFilter; // for tests
    public completions = input<IOnboardingReportParticipantDetails>();
    public highlightedStepId = model<number | null>(null);

    protected readonly circleFillColor = '#2196F3';
    protected readonly circleSize = 36;
    protected readonly circleStrokeWidth = 4;
    protected readonly circleAnimationDuration = 200;
    protected readonly circleFillDirection = 'right';
    protected readonly circleFullFillLightColor = '#29CC6A';
    protected readonly circleFullFillDarkColor = '#18C45D';

    private readonly fiOnboardingReportActionsBuilderService = inject(FiOnboardingReportActionsBuilderService);
    private readonly fiOnboardingCompletionHelperService = inject(FiOnboardingCompletionHelperService);
    private readonly fiOnboardingCompletionSyncService = inject(FiOnboardingCompletionSyncService);

    protected actions: IItemsListAction2<IOnboardingReportParticipantDetails>[];
    private notCompletedBeforeStepIds: number[] = [];

    protected userProfile = computed<IUserProfile>(() => {
        return this.completions().UserCompletions[0].UserProfile;
    });

    protected onboardingId = computed<number>(() => {
        return this.completions().UserCompletions[0].OnboardingId;
    });

    protected totalStepsCount = computed<number>(() => {
        return this.sections()
            .filter((section) => !section.IsDisabled)
            .flatMap((section) => section.Steps)
            .filter((step) => !step.IsDisabled).length;
    });

    protected sections = computed<IOnboardingCompletionSection[]>(() => {
        return this.completions().OnboardingsToComplete.flatMap((onboarding) =>
            onboarding.Sections.filter((section) => !section.IsDisabled),
        );
    });

    protected onboardingName = computed<string>(() => {
        return this.completions().OnboardingsToComplete[0].Name;
    });

    protected allAvailableSteps = computed<IOnboardingCompletionStep[]>(() => {
        return this.sections()
            .flatMap((section) => this.getSteps(section))
            .filter((step) => !step.IsDisabled);
    });

    private activeStep = computed<IOnboardingCompletionUserStep | undefined>(() => {
        return this.getAllUserCompletionSteps().find(
            (step) =>
                step.CompletionState === FiOnboardingCompletionStateType.InProgress ||
                step.CompletionState === FiOnboardingCompletionStateType.NotCompleted,
        );
    });

    protected userCompletionStepsMap = computed<Map<number, IOnboardingCompletionUserStep>>(() => {
        return new Map(this.getAllUserCompletionSteps().map((step) => [step.OnboardingStepId, step]));
    });

    protected completedStepsMap = computed<Map<number, IOnboardingCompletionUserStep>>(() => {
        return new Map(
            this.getAllUserCompletionSteps()
                .filter((step) => step.CompletionState === FiOnboardingCompletionStateType.Completed)
                .map((step) => [step.OnboardingStepId, step]),
        );
    });

    private currentOpenedCompletionStep: Signal<ICurrentOpenedCompletionStepData | undefined> =
        this.fiOnboardingCompletionSyncService.currentOpenedCompletionStepData;

    protected readonly stepContainerElementId = 'step-container-{0}';
    private readonly resetHighlightedStepIdTimeout = 5000;

    private getAllUserCompletionSteps(): IOnboardingCompletionUserStep[] {
        const enabledStepIds = new Set(
            this.sections().flatMap((section) => this.getSteps(section).map((step) => step.OnboardingStepId)),
        );

        return this.completions()
            .UserCompletions.flatMap((userCompletion) => userCompletion.Steps)
            .filter((step) => enabledStepIds.has(step.OnboardingStepId));
    }

    protected completedCount = computed<number>(() => {
        return this.completedStepsMap().size;
    });

    protected completedFraction = computed<number>(() => {
        return Math.round((this.completedCount() / this.totalStepsCount()) * 100);
    });

    /*protected onboardingTotalStepsCount = computed<number>(() => {
        return this.completions().OnboardingsToComplete.reduce(
            (acc, onboarding) => acc + onboarding.Sections.reduce((acc, section) => acc + section.Steps.length, 0),
            0,
        );
    });*/

    public ngOnInit(): void {
        this.actions = this.fiOnboardingReportActionsBuilderService.getParticipantDetailsActions();
        this.initNotCompletedBeforeSteps();
        this.openStepCompletionDialogIfAvailable();
    }

    public ngAfterViewInit(): void {
        this.scrollToHighlightedStep();
    }

    private scrollToHighlightedStep(): void {
        if (isNullOrUndefined(this.highlightedStepId())) {
            return;
        }
        setTimeout(() => {
            this.highlightedStepId.set(null);
        }, this.resetHighlightedStepIdTimeout);
        const stepContainerElement = document.getElementById(
            this.stepContainerElementId.replace('{0}', this.highlightedStepId().toString()),
        );
        if (isNullOrUndefined(stepContainerElement)) {
            return;
        }
        stepContainerElement.scrollIntoView({ behavior: 'smooth' });
    }

    private openStepCompletionDialogIfAvailable(): void {
        const currentOpenedCompletionStepData =
            this.fiOnboardingCompletionSyncService.currentOpenedCompletionStepData();
        if (!currentOpenedCompletionStepData?.isReportMode) {
            this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData(undefined);
        } else {
            this.openStepCompletionDialog(this.currentOpenedCompletionStep().onboardingCompletionStep);
        }
    }

    private initNotCompletedBeforeSteps(): void {
        let foundNotCompletedBeforeStep = false;
        this.sections().forEach((section) => {
            this.getSteps(section).forEach((step) => {
                if (!foundNotCompletedBeforeStep) {
                    const completedStep = this.getCompletedUserStepById(step.OnboardingStepId);
                    if (!isDefined(completedStep)) {
                        foundNotCompletedBeforeStep = true;
                    }
                } else {
                    this.notCompletedBeforeStepIds.push(step.OnboardingStepId);
                }
            });
        });
    }

    protected getSteps(section: IOnboardingCompletionSection): IOnboardingCompletionStep[] {
        return section.Steps.filter((step) => !step.IsDisabled);
    }

    protected isCompletedStepLocked(stepId: number): boolean {
        return this.notCompletedBeforeStepIds.includes(stepId);
    }

    protected getCompletedUserStepById(stepId: number): IOnboardingCompletionUserStep | undefined {
        return this.completedStepsMap().get(stepId);
    }

    protected isStepInProgress(stepId: number): boolean {
        return (
            this.userCompletionStepsMap().get(stepId)?.CompletionState === FiOnboardingCompletionStateType.InProgress
        );
    }

    protected isStepActive(step: IOnboardingCompletionStep): boolean {
        return this.activeStep()?.OnboardingStepId === step.OnboardingStepId;
    }

    protected openStepCompletionDialog(step: IOnboardingCompletionStep): void {
        this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData({
            onboardingCompletionStep: step,
            onboardingId: this.onboardingId(),
            stepId: step.OnboardingStepId,
            isHomePage: false,
            isReportMode: true,
        });

        this.fiOnboardingCompletionHelperService
            .openStepCompletionDialog({
                onboardingId: this.onboardingId(),
                userId: this.userProfile().Id,
                step: step,
                userCompletionStep: this.userCompletionStepsMap().get(step.OnboardingStepId),
                nextStepCompletionState: undefined,
                isReportMode: true,
            })
            .onYesButtonClick(() => {
                this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData(undefined);
            });
    }

    protected back(): void {
        window.history.back();
    }

    protected readonly FiProfileSizes = FiProfileSizes;
    protected getIconClassByObjectType = getIconClassByObjectType;
}
